import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Loader2, ChevronDown, ScrollText } from "lucide-react";
import AdBanner from "@/components/AdBanner";
import ShareAnswerButton from "@/components/ShareAnswerButton";
import { useAuth } from "@/context/AuthContext";

export default function History() {
  const { onSubscribe } = useOutletContext();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/readings/history").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, []);

  if (items === null) {
    return (
      <div className="flex justify-center py-32">
        <Loader2 className="animate-spin gold-text" size={28} />
      </div>
    );
  }

  const renderContent = (c) => {
    if (typeof c === "string") return <p className="text-base leading-relaxed text-[#D6D0C5] whitespace-pre-wrap">{c}</p>;
    // daily reading object
    return (
      <div className="space-y-2 text-base leading-relaxed text-[#D6D0C5]">
        <p>{c.overall}</p>
        <p><span className="gold-text">Love · </span>{c.love}</p>
        <p><span className="gold-text">Money · </span>{c.money}</p>
        <p><span className="gold-text">Weekly · </span>{c.weekly}</p>
        <p><span className="gold-text">Monthly · </span>{c.monthly}</p>
      </div>
    );
  };

  return (
    <div className="px-5 pt-5 fade-up">
      <p className="text-[11px] uppercase tracking-[0.3em] gold-text">Your Archive</p>
      <h1 className="text-3xl font-light mb-5">Chronicles of Contemplation</h1>

      <AdBanner slot="3530801375" onSubscribe={onSubscribe} className="mb-5" />

      {items.length === 0 ? (
        <div className="flex flex-col items-center text-center py-16 text-[#A7B0C0]">
          <ScrollText size={34} className="gold-text mb-3 opacity-70" />
          <p className="italic">No readings saved yet.</p>
          <p className="text-sm">Save today's reading or unlock an in-depth one to begin your archive.</p>
        </div>
      ) : (
        <div data-testid="history-readings-list" className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="bg-[#0F1523] border border-[#D4AF37]/15 rounded-2xl overflow-hidden">
              <button
                onClick={() => setOpen(open === it.id ? null : it.id)}
                className="w-full flex items-center justify-between p-4 text-left"
                data-testid={`history-item-${it.id}`}
              >
                <div>
                  <p className="text-lg">{it.title}</p>
                  <p className="text-xs text-[#A7B0C0]">{it.date}</p>
                </div>
                <ChevronDown size={18} className={`gold-text transition-transform ${open === it.id ? "rotate-180" : ""}`} />
              </button>
              {open === it.id && (
                <div className="px-4 pb-4">
                  {renderContent(it.content)}
                  {it.kind === "synastry" && it.report_id && (
                    <Link to={`/synastry/${it.report_id}`} data-testid={`history-open-synastry-${it.id}`} className="inline-block mt-3 text-sm gold-text">Open the full report with charts →</Link>
                  )}
                  {it.kind === "chat" && it.source_id && !user?.is_demo && (
                    <ShareAnswerButton messageId={it.source_id} testId={`history-share-${it.id}`} className="mt-3 text-sm" />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
